'use client';
// "Order from the app": phones go to their store, desktops get a sheet with a QR code of the /go deep link.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import qrcode from 'qrcode-generator';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '@/lib/v9/appHandoff';
import { appHandoff } from '@/lib/v9/appHandoff';
import { Apple, Close, GooglePlay } from './icons';
import { Price, cx, useEscape, useScrollLock } from './ui';

export type HandoffItem = {
  title?: string;
  vendor?: string;
  description?: string;
  image?: string | null;
  price?: number | string | null;
  /** The catalog item to open in the app (otherwise the app opens on its home). */
  itemId?: string;
};

type Ctx = { openApp: (item?: HandoffItem) => void };
const HandoffContext = createContext<Ctx>({ openApp: () => {} });
export const useAppHandoff = () => useContext(HandoffContext);

export function StoreBadges({ dark, compact, className }: { dark?: boolean; compact?: boolean; className?: string }) {
  const t = useTranslations('v9.common');
  const badge = cx(
    'inline-flex items-center gap-3 rounded-[18px] transition-[box-shadow,transform] duration-300 ease-mila hover:-translate-y-0.5',
    compact ? 'min-h-[52px] rounded-[14px] px-3.5 py-1.5' : 'min-h-[60px] px-[22px] py-[9px] shadow-[0_18px_40px_-22px_rgba(0,0,0,.6)]',
    dark ? 'bg-deep text-white' : 'bg-white text-deep',
  );
  return (
    <div className={cx('flex flex-wrap gap-3', compact && 'gap-2', className)}>
      <a className={badge} href={APP_STORE_URL} target="_blank" rel="noopener">
        <Apple className="h-7 w-7 flex-none" />
        <span className="flex flex-col text-start leading-tight">
          <small className="text-[11.5px] font-medium opacity-80">{t('downloadOn')}</small>
          <b dir="ltr" className="text-[18px] font-extrabold tracking-[-.01em]">App Store</b>
        </span>
      </a>
      <a className={badge} href={GOOGLE_PLAY_URL} target="_blank" rel="noopener">
        <GooglePlay className="h-7 w-7 flex-none" />
        <span className="flex flex-col text-start leading-tight">
          <small className="text-[11.5px] font-medium opacity-80">{t('getItOn')}</small>
          <b dir="ltr" className="text-[18px] font-extrabold tracking-[-.01em]">Google Play</b>
        </span>
      </a>
    </div>
  );
}

export function QrCode({ value, className }: { value: string; className?: string }) {
  const svg = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(value);
    qr.make();
    const n = qr.getModuleCount();
    let d = '';
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c} ${r}h1v1h-1z`;
    return `<svg viewBox="0 0 ${n} ${n}" shape-rendering="crispEdges" aria-hidden="true"><path fill="#39164D" d="${d}"/></svg>`;
  }, [value]);
  return <div className={cx('[&_svg]:h-full [&_svg]:w-full', className)} dangerouslySetInnerHTML={{ __html: svg }} />;
}

function HandoffSheet({ item, url, onClose }: { item: HandoffItem; url: string; onClose: () => void }) {
  const t = useTranslations('v9.common');
  useScrollLock(true);
  useEscape(onClose);
  const hasItem = !!item.title;
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center overflow-auto bg-[rgba(30,10,46,.72)] p-4 backdrop-blur-[8px]" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={item.title || t('appSheetTitle')}
        onClick={(e) => e.stopPropagation()}
        className={cx(
          'relative grid max-h-[calc(100svh-32px)] w-[min(100%,900px)] overflow-auto rounded-[28px] bg-white text-deep shadow-[0_50px_100px_-30px_rgba(0,0,0,.6)] animate-[v9rise_.35s_var(--ease)]',
          hasItem ? 'md:grid-cols-[minmax(0,.9fr)_minmax(0,1.1fr)]' : 'w-[min(100%,520px)]',
        )}
      >
        <button type="button" onClick={onClose} aria-label={t('close')} className="absolute end-3 top-3 z-[3] grid h-11 w-11 place-items-center rounded-full bg-white text-deep shadow-[0_6px_18px_-8px_rgba(57,22,77,.5)]">
          <Close className="h-5 w-5" />
        </button>
        {hasItem && (
          <div className="relative min-h-[260px] bg-panel">
            {item.image && <img loading="lazy" src={item.image} alt="" className="absolute inset-0 h-full w-full object-cover" />}
          </div>
        )}
        <div className="flex flex-col gap-2 p-[clamp(20px,3vw,34px)]">
          {item.vendor && <p className="pe-12 font-body text-[14px] text-deep/[.66]">{item.vendor}</p>}
          <h2 className="pe-10 text-[clamp(22px,2.4vw,30px)] font-extrabold leading-[1.5]">{item.title || t('appSheetTitle')}</h2>
          {item.description && <p className="max-h-[9.6em] overflow-auto font-body text-[15px] leading-[1.9] text-deep/[.78]">{item.description}</p>}
          {item.price != null && <p className="text-[28px] font-extrabold text-plum"><Price amount={item.price} /></p>}
          <div className="mt-2.5 flex items-center gap-4 rounded-[22px] bg-panel p-3.5">
            <QrCode value={url} className="h-28 w-28 flex-none rounded-[14px] bg-white p-2" />
            <div className="flex min-w-0 flex-col gap-1.5">
              <b className="text-[18px] font-extrabold leading-[1.5]">{t('orderInApp')}</b>
              <small className="font-body text-[13.5px] leading-[1.7] text-deep/[.72]">{t('scanToOrder')} · {t('appSheetNote')}</small>
              <StoreBadges dark compact className="mt-1" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AppHandoffProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<{ item: HandoffItem; url: string } | null>(null);
  const openApp = useCallback((item: HandoffItem = {}) => {
    const where = appHandoff(navigator.userAgent, item.itemId);
    if (where.kind === 'redirect') {
      window.location.href = where.url;
      return;
    }
    setOpen({ item, url: where.url });
  }, []);
  const ctx = useMemo(() => ({ openApp }), [openApp]);
  return (
    <HandoffContext.Provider value={ctx}>
      {children}
      {open && <HandoffSheet item={open.item} url={open.url} onClose={() => setOpen(null)} />}
    </HandoffContext.Provider>
  );
}
